import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppContext } from "../../contexts/AppContext";
import { useSelection } from "../../hooks/useSelection";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { useFieldConfig } from "../../contexts/FieldConfigContext";
import { useFiles } from "../../hooks";
import {
  EXIF_FIELDS,
  FIELD_CATEGORIES,
  CATEGORY_METADATA,
} from "../../config/fieldDefinitions";
import { validateFieldValue, validateGPSPair } from "../../utils/fieldValidation";
import { areDateTakenEqual } from "../../utils/dateTaken";

export const QUICK_EDIT_FIELD_IDS = [
  "dateTaken",
  "latitude",
  "longitude",
  "cameraMake",
  "cameraModel",
  "lensModel",
  "copyright",
];

export function useEditorMetadataModel() {
  const {
    files,
    setFiles,
    setLocationModalOpen,
    setPresetModalOpen,
    setSelectedCellRange,
  } = useAppContext();
  const { selectedIndices, primaryIndex } = useSelection();
  const { recordAction } = useUndoRedo();
  const { config, getFieldsByCategory } = useFieldConfig();
  const { removeFiles } = useFiles();

  const hasInitializedOpenCategoriesRef = useRef(false);

  const [formData, setFormData] = useState(() => {
    const initial = {};
    Object.values(EXIF_FIELDS).forEach((field) => {
      initial[field.id] = field.defaultValue;
    });
    return initial;
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [openCategoryIds, setOpenCategoryIds] = useState([]);

  const enabledFieldsByCategory = useMemo(() => {
    const result = {};
    config.categoryOrder.forEach((categoryId) => {
      const fields = getFieldsByCategory(categoryId, true);
      if (fields.length > 0) {
        result[categoryId] = fields;
      }
    });
    return result;
  }, [config.categoryOrder, getFieldsByCategory]);

  const visibleCategoryIds = useMemo(
    () =>
      config.categoryOrder.filter(
        (categoryId) => (enabledFieldsByCategory[categoryId] || []).length > 0,
      ),
    [config.categoryOrder, enabledFieldsByCategory],
  );

  const selectedIndicesArray = useMemo(
    () => Array.from(selectedIndices),
    [selectedIndices],
  );

  const selectedFiles = useMemo(
    () => selectedIndicesArray.map((index) => files[index]).filter(Boolean),
    [files, selectedIndicesArray],
  );

  const areFieldValuesEqual = useCallback((fieldId, left, right) => {
    if (fieldId === "dateTaken") {
      return areDateTakenEqual(left, right);
    }
    return left === right;
  }, []);

  const hasAnyChanges = useCallback(
    (file) => {
      const keys = new Set([
        ...Object.keys(file.modifiedExifData || {}),
        ...Object.keys(file.exifData || {}),
      ]);

      for (const key of keys) {
        if (
          !areFieldValuesEqual(
            key,
            file.modifiedExifData?.[key],
            file.exifData?.[key],
          )
        ) {
          return true;
        }
      }
      return false;
    },
    [areFieldValuesEqual],
  );

  const hasMultiple = useMemo(() => {
    const result = {};
    Object.values(EXIF_FIELDS).forEach((field) => {
      result[field.id] =
        new Set(selectedFiles.map((file) => file.modifiedExifData[field.id])).size >
        1;
    });
    return result;
  }, [selectedFiles]);

  const isFieldEdited = useMemo(() => {
    const result = {};
    Object.values(EXIF_FIELDS).forEach((field) => {
      result[field.id] = selectedFiles.some((file) => {
        const originalValue = file.exifData?.[field.id] ?? field.defaultValue;
        const currentValue = file.modifiedExifData?.[field.id] ?? field.defaultValue;
        return originalValue !== currentValue;
      });
    });
    return result;
  }, [selectedFiles]);

  useEffect(() => {
    if (selectedFiles.length === 0) return;

    const firstFile = selectedFiles[0].modifiedExifData;
    const nextFormData = {};

    Object.values(EXIF_FIELDS).forEach((field) => {
      nextFormData[field.id] = hasMultiple[field.id]
        ? field.defaultValue
        : (firstFile[field.id] ?? field.defaultValue);
    });

    setFormData(nextFormData);
    setFieldErrors({});
  }, [selectedFiles, hasMultiple]);

  useEffect(() => {
    setOpenCategoryIds((prev) => {
      if (!hasInitializedOpenCategoriesRef.current) {
        hasInitializedOpenCategoriesRef.current = true;
        return visibleCategoryIds;
      }

      const visibleSet = new Set(visibleCategoryIds);
      const filtered = prev.filter((id) => visibleSet.has(id));
      const next = [...filtered];
      const nextSet = new Set(next);

      visibleCategoryIds.forEach((categoryId) => {
        if (!nextSet.has(categoryId)) {
          next.push(categoryId);
        }
      });

      return next;
    });
  }, [visibleCategoryIds]);

  const clearFieldError = useCallback((fieldId) => {
    setFieldErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const applyBulkUpdatesToIndices = useCallback(
    (indices, updates, actionLabel) => {
      if (!indices || indices.length === 0) return;

      const sanitizedUpdates = Object.fromEntries(
        Object.entries(updates).filter(([, value]) => value !== undefined),
      );
      if (Object.keys(sanitizedUpdates).length === 0) return;

      const targetSet = new Set(indices);
      const previousFiles = files.map((file) => ({
        ...file,
        modifiedExifData: { ...file.modifiedExifData },
      }));

      const applyToFiles = (sourceFiles) =>
        sourceFiles.map((file, index) => {
          if (!targetSet.has(index)) return file;

          const nextFile = {
            ...file,
            modifiedExifData: {
              ...file.modifiedExifData,
              ...sanitizedUpdates,
            },
          };

          return {
            ...nextFile,
            hasChanges: hasAnyChanges(nextFile),
          };
        });

      setFormData((prev) => ({ ...prev, ...sanitizedUpdates }));
      setFiles((prev) => applyToFiles(prev));

      recordAction(
        actionLabel,
        () => setFiles(previousFiles),
        () => {
          setFormData((prev) => ({ ...prev, ...sanitizedUpdates }));
          setFiles((prev) => applyToFiles(prev));
        },
        indices,
      );
    },
    [files, setFiles, recordAction, hasAnyChanges],
  );

  const applyPerIndexUpdates = useCallback(
    (updatesByIndex, actionLabel) => {
      const entries = Object.entries(updatesByIndex || {});
      if (entries.length === 0) return;

      const targetIndices = entries.map(([index]) => Number(index));
      const previousFiles = files.map((file) => ({
        ...file,
        modifiedExifData: { ...file.modifiedExifData },
      }));

      const applyToFiles = (sourceFiles) =>
        sourceFiles.map((file, index) => {
          const updates = updatesByIndex[index];
          if (!updates) return file;

          const nextFile = {
            ...file,
            modifiedExifData: {
              ...file.modifiedExifData,
              ...updates,
            },
          };

          return {
            ...nextFile,
            hasChanges: hasAnyChanges(nextFile),
          };
        });

      setFiles((prev) => applyToFiles(prev));

      recordAction(
        actionLabel,
        () => setFiles(previousFiles),
        () => setFiles((prev) => applyToFiles(prev)),
        targetIndices,
      );
    },
    [files, setFiles, recordAction, hasAnyChanges],
  );

  const handleChange = useCallback(
    (fieldId, value) => {
      setFormData((prev) => ({ ...prev, [fieldId]: value }));
      clearFieldError(fieldId);
    },
    [clearFieldError],
  );

  const handleCommit = useCallback(
    (fieldId, explicitValue) => {
      if (selectedIndices.size === 0) return false;

      const rawValue = explicitValue !== undefined ? explicitValue : formData[fieldId];
      const validation = validateFieldValue(fieldId, rawValue);

      if (!validation.valid) {
        setFieldErrors((prev) => ({
          ...prev,
          [fieldId]: validation.error || "Invalid value",
        }));
        return false;
      }

      clearFieldError(fieldId);

      if (fieldId === "latitude" || fieldId === "longitude") {
        const latitude =
          fieldId === "latitude" ? validation.processedValue : formData.latitude;
        const longitude =
          fieldId === "longitude" ? validation.processedValue : formData.longitude;

        const gpsValidation = validateGPSPair(latitude, longitude);
        if (gpsValidation.warnings.length > 0) {
          console.warn("GPS coordinate warning:", gpsValidation.warnings.join(", "));
        }
      }

      const processedValue = validation.processedValue;
      const affectedIndices = selectedIndicesArray;
      const hasAnyChange = affectedIndices.some(
        (index) => files[index]?.modifiedExifData?.[fieldId] !== processedValue,
      );

      setFormData((prev) => ({ ...prev, [fieldId]: processedValue }));

      if (!hasAnyChange) return true;

      applyBulkUpdatesToIndices(
        affectedIndices,
        { [fieldId]: processedValue },
        `Edit ${fieldId} for ${affectedIndices.length} file${affectedIndices.length > 1 ? "s" : ""}`,
      );

      return true;
    },
    [
      selectedIndices,
      selectedIndicesArray,
      formData,
      files,
      applyBulkUpdatesToIndices,
      clearFieldError,
    ],
  );

  const handleRevertField = useCallback(
    (fieldId) => {
      const field = EXIF_FIELDS[fieldId];
      const fallback = field?.defaultValue;
      const nextValue = hasMultiple[fieldId]
        ? fallback
        : (selectedFiles[0]?.modifiedExifData?.[fieldId] ?? fallback);

      setFormData((prev) => ({ ...prev, [fieldId]: nextValue }));
      clearFieldError(fieldId);
    },
    [hasMultiple, selectedFiles, clearFieldError],
  );

  const handleOpenLocationModal = useCallback(() => {
    setSelectedCellRange(null);
    setLocationModalOpen(true);
  }, [setSelectedCellRange, setLocationModalOpen]);

  const handleOpenPresetModal = useCallback(() => {
    setSelectedCellRange(null);
    setPresetModalOpen(true);
  }, [setSelectedCellRange, setPresetModalOpen]);

  const categoryHasValues = useCallback(
    (categoryId) => {
      const fields = enabledFieldsByCategory[categoryId] || [];
      return fields.some((field) => {
        const value = formData[field.id];
        return (
          value !== null &&
          value !== "" &&
          value !== undefined &&
          value !== field.defaultValue
        );
      });
    },
    [enabledFieldsByCategory, formData],
  );

  const handleClearCategory = useCallback(
    (categoryId) => {
      if (selectedIndices.size === 0) return;

      const fields = enabledFieldsByCategory[categoryId] || [];
      if (fields.length === 0) return;

      const updates = {};
      fields.forEach((field) => {
        updates[field.id] = field.defaultValue;
      });

      const count = selectedIndices.size;
      const categoryLabel = CATEGORY_METADATA[categoryId]?.label || categoryId;

      applyBulkUpdatesToIndices(
        selectedIndicesArray,
        updates,
        `Clear ${categoryLabel} for ${count} file${count === 1 ? "" : "s"}`,
      );
    },
    [
      selectedIndices.size,
      selectedIndicesArray,
      enabledFieldsByCategory,
      applyBulkUpdatesToIndices,
    ],
  );

  const handleRestoreOriginalMetadata = useCallback(() => {
    if (selectedIndices.size === 0) return;

    const updatesByIndex = {};
    selectedIndicesArray.forEach((index) => {
      const file = files[index];
      if (!file) return;

      const updates = {};
      config.categoryOrder.forEach((categoryId) => {
        const fields = enabledFieldsByCategory[categoryId] || [];
        fields.forEach((field) => {
          updates[field.id] = file.exifData?.[field.id] ?? null;
        });
      });

      updatesByIndex[index] = updates;
    });

    const count = selectedIndices.size;
    applyPerIndexUpdates(
      updatesByIndex,
      `Restore original metadata for ${count} file${count === 1 ? "" : "s"}`,
    );
  }, [
    selectedIndices.size,
    selectedIndicesArray,
    files,
    config.categoryOrder,
    enabledFieldsByCategory,
    applyPerIndexUpdates,
  ]);

  const handleClearAllMetadata = useCallback(() => {
    if (selectedIndices.size === 0) return;

    const updates = {};
    config.categoryOrder.forEach((categoryId) => {
      const fields = enabledFieldsByCategory[categoryId] || [];
      fields.forEach((field) => {
        updates[field.id] = null;
      });
    });

    const count = selectedIndices.size;
    applyBulkUpdatesToIndices(
      selectedIndicesArray,
      updates,
      `Clear all metadata for ${count} file${count === 1 ? "" : "s"}`,
    );
  }, [
    selectedIndices.size,
    selectedIndicesArray,
    config.categoryOrder,
    enabledFieldsByCategory,
    applyBulkUpdatesToIndices,
  ]);

  const handleRemoveSelectedFiles = useCallback(() => {
    if (selectedIndices.size === 0) return;
    removeFiles(selectedIndices);
  }, [removeFiles, selectedIndices]);

  return {
    files,
    primaryIndex,
    selectedIndices,
    selectedIndicesArray,
    selectedFiles,
    enabledFieldsByCategory,
    visibleCategoryIds,
    formData,
    fieldErrors,
    hasMultiple,
    isFieldEdited,
    openCategoryIds,
    setOpenCategoryIds,
    handleChange,
    handleCommit,
    handleRevertField,
    categoryHasValues,
    handleClearCategory,
    handleRestoreOriginalMetadata,
    handleClearAllMetadata,
    handleRemoveSelectedFiles,
    handleOpenLocationModal,
    handleOpenPresetModal,
    applyBulkUpdatesToIndices,
    applyPerIndexUpdates,
    quickFieldIds: QUICK_EDIT_FIELD_IDS,
    locationCategoryId: FIELD_CATEGORIES.LOCATION,
  };
}

export default useEditorMetadataModel;
